import { getFetcher, postFetcher } from '@/api/fetchers';
import { createKey } from '@/api/params';
import { Badge } from '@/components/Badge/Badge';
import { BooleanBadge } from '@/components/Badge/BooleanBadge';
import { AlignedCell } from '@/components/List/Cell/AlignedCell';
import { ClampedTextCell } from '@/components/List/Cell/ClampedTextCell';
import { SortHeader } from '@/components/List/Header/SortHeader';
import { TextHeader } from '@/components/List/Header/TextHeader';
import { List } from '@/components/List/List';
import {
  type PlanningContextType,
  StateContext,
  type PlanningState,
} from '@/layouts/Planning/PlanningLayout';
import { type ClientPlansRequest } from '@/schemas/API/GET/client-plan/request';
import {
  type ClientPlan,
  type ClientPlans,
} from '@/schemas/API/GET/client-plan/response';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import { displayError } from '@/utils/error/errorHandler';
import { Text, Input, Button, Stack, Select } from '@mantine/core';
import { useContext, useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

const PlanningNameCell = (plan: ClientPlan) => (
  <ClampedTextCell>
    {plan.client.lastName} {plan.client.firstName}
  </ClampedTextCell>
);

const PlanningTasksRatioCell = (plan: ClientPlan) => (
  <AlignedCell align="center">
    <BooleanBadge
      failLabel={`${plan.info.assignTasksCount}/${plan.info.tasksCount}`}
      successLabel={`${plan.info.assignTasksCount}/${plan.info.tasksCount}`}
      successCond={() => plan.info.assignTasksCount === plan.info.tasksCount}
    />
  </AlignedCell>
);

const OptRenderCell = (state: PlanningState, stateMap: Record<string, string> | undefined) => {
  const Cell = (plan: ClientPlan) => {
    switch (state) {
      case 'future':
        return (
          <AlignedCell align="center">
            <Badge
              label={stateMap?.[plan.state] ?? 'Neuvedený'}
              color={plan.state === 'PLANNED' ? 'success' : 'warning'}
            />
          </AlignedCell>
        );
      case 'present':
        return <></>;
      case 'past':
        return <></>;
    }
  };
  return Cell;
};

export const PlanningListPage = (): ReactElement => {
  const navigate = useNavigate();
  const { state, service, planId, setClientPlanId, month, setClientName } =
    useContext<PlanningContextType>(StateContext);
  const [requestParams, setRequestParams] = useState<ClientPlansRequest>({
    page: 1,
    sort: [],
    size: 10,
  });

  const [clientPlans, setClientPlans] = useState<ClientPlans | undefined>(
    undefined
  );

  const setRequestParam = <K extends keyof ClientPlansRequest>(
    fieldName: K,
    value: ClientPlansRequest[K] | null
  ): void => {
    setRequestParams(prevParams => {
      return { ...prevParams, [fieldName]: value };
    });
  };

  const { data: stateMap } = useSWR<Mapping>(
    'mappings/client-plan-states',
    getFetcher
  );

  useEffect(() => {
    const fetchData = async () => {
      if (planId) {
        try {
          const clientPlans = await getFetcher(
            `services/${service}/plans/${planId}/client-plans` +
              createKey(requestParams) +
              `&assignTasksDiff=desc`
          );
          setClientPlans(clientPlans);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      } else {
        setClientPlans(undefined);
      }
    };
    fetchData();
  }, [planId, requestParams]);

  useEffect(() => {
    navigate(createKey(requestParams), { replace: true });
  }, [requestParams, navigate]);

  useEffect(() => {
    return () => {
      setClientPlans(undefined);
    };
  }, []);

  useEffect(() => {
    setRequestParams({
      page: 1,
      sort: [],
      size: 10,
    });
  }, [service, month]);

  const SearchBar = (
    <Input.Wrapper label="Vyhledat">
      <Input
        placeholder="např: Václav"
        value={requestParams?.query}
        onChange={e => setRequestParam('query', e.currentTarget.value)}
      />
    </Input.Wrapper>
  );

  async function createPlan(): Promise<void> {
    try {
      setIsGenerating(true);
      await postFetcher(`/services/${service}/plans`, {
        month: month.get('month') + 1,
        year: month.get('year'),
      });

      // reload is necessary as rendered content depends on const context (planId)
      localStorage.setItem('state', JSON.stringify(state));
      localStorage.setItem('month', month.toISOString());
      window.location.reload();
    } catch (err) {
      displayError(err);
    } finally {
      setIsGenerating(false);
    }
  }

  const StateSelector = (
    <Select
      label="Stav"
      placeholder="Všechny"
      value={requestParams.state && stateMap ? requestParams.state : null}
      onChange={selected => setRequestParam('state', selected ?? null)}
      data={stateMap ? MappingToComboBoxItem(stateMap) : []}
      clearable
    />
  );

  const optHeader = () => {
    switch (state) {
      case 'future':
        return <TextHeader align="center">Stav</TextHeader>;
      case 'present':
        return <></>;
      case 'past':
        return <></>;
    }
  };

  const [isGenerating, setIsGenerating] = useState(false);

  const handleRowClick = (clientPlan: ClientPlan) => {
    setClientPlanId(clientPlan.id);
    setClientName(
      clientPlan.client.firstName + ' ' + clientPlan.client.lastName
    );
    switch (state) {
      case 'past':
        navigate('/planning/plans');
        break;
      case 'present':
        navigate('/planning/plan-adjustment');
        break;
      case 'future':
        switch (clientPlan.state) {
          case 'NOT_PLANNED':
            navigate('/planning/planning');
            break;
          case 'TO_PLAN':
            navigate('/planning/for-planning');
            break;
          case 'PLANNED':
            navigate('/planning/planned');
            break;
        }
        break;
    }
  };

  if (!planId) {
    return (
      <Stack align="center" className="flex-1 justify-center">
        <Text>Plán pro tento měsíc ještě nebyl vytvořen.</Text>
        <Button variant="light" onClick={createPlan} disabled={isGenerating}>
          {isGenerating ? 'Probíhá generování...' : 'Vytvořit nový plán'}
        </Button>
      </Stack>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <List
        pagination={{
          page: Number(requestParams.page),
          setPage: e => setRequestParam('page', e),
          pageTotal: clientPlans?.totalPages ?? 1,
        }}
        loading={!clientPlans}
        perPageSelector={{
          label: 'Klientů na stránce',
          value: requestParams.size ?? 0,
          setter: v => setRequestParam('size', v),
        }}
        selectors={
          state === 'future' ? [SearchBar, StateSelector] : [SearchBar]
        }
        columns={[
          <SortHeader
            key="name"
            label="Příjmení a jméno"
            fields={['firstName', 'lastName']}
            callback={sort => setRequestParam('sort', sort)}
            currentSort={requestParams.sort ?? []}
          />,
          <TextHeader key="events" align="center">
            Události
          </TextHeader>,
          optHeader(),
        ]}
        content={{
          items: clientPlans?.content ?? [],
          onClick: handleRowClick,
          renders: [PlanningNameCell, PlanningTasksRatioCell, OptRenderCell(state, stateMap)],
        }}
      />
    </div>
  );
};
